import React, { useMemo, useState } from "react";
import { ActionIcon, Group, Skeleton, Table, Text, Title, UnstyledButton } from "@mantine/core";
import { IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { DistributorComparison } from "types/analyticsTypes";
import { SortDirection, sortByDemoCount } from "./distributorComparison";

interface DistributorComparisonTableProps {
  data: DistributorComparison | null;
  countryByDistId?: Record<string, string | undefined>;
  onDistributorClick?: (distId: string) => void;
}

const formatAverage = (value: number | null) =>
  value === null ? "–" : value.toLocaleString(undefined, { maximumFractionDigits: 1 });

const DistributorComparisonTable: React.FC<DistributorComparisonTableProps> = ({
  data,
  countryByDistId,
  onDistributorClick,
}) => {
  const { t } = useTranslation();
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const rows = useMemo(
    () => sortByDemoCount(data?.distributors ?? [], sortDirection),
    [data, sortDirection],
  );
  const sortLabel = t(`analytics.sort.${sortDirection === "asc" ? "ascending" : "descending"}`);

  return (
    <Skeleton visible={!data}>
      <Group justify="space-between" mb="xs" wrap="wrap" gap="sm">
        <Title order={3}>{t("analytics.comparison.title")}</Title>
        <ActionIcon
          variant="subtle"
          onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
          title={sortLabel}
          aria-label={sortLabel}
        >
          {sortDirection === "asc" ? <IconSortAscending /> : <IconSortDescending />}
        </ActionIcon>
      </Group>
      {data && (
        <Text size="sm" c="dimmed" mb="md">
          {t("analytics.comparison.periodNote", {
            start: data.period.start.slice(0, 10),
            end: data.period.end.slice(0, 10),
            count: Math.round(data.period.months * 10) / 10,
            included: data.includedDistributors,
            total: data.totalDistributors,
          })}
        </Text>
      )}
      {data && rows.length === 0 ? (
        <Text c="dimmed">{t("analytics.comparison.noData")}</Text>
      ) : (
        <Table.ScrollContainer minWidth={520}>
          <Table highlightOnHover withRowBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("analytics.comparison.distributor")}</Table.Th>
                <Table.Th ta="right">{t("analytics.comparison.demoAccounts")}</Table.Th>
                <Table.Th ta="right">{t("analytics.comparison.demoTests")}</Table.Th>
                <Table.Th ta="right">{t("analytics.comparison.monthlyAverage")}</Table.Th>
                <Table.Th ta="right">{t("analytics.comparison.yearlyAverage")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.distId}>
                  <Table.Td>
                    <UnstyledButton onClick={() => onDistributorClick?.(row.distId)}>
                      <Text size="sm" fw={500} c="var(--mantine-primary-color-filled)">
                        {countryByDistId?.[row.distId]
                          ? `${row.distId} (${countryByDistId[row.distId]})`
                          : row.distId}
                      </Text>
                    </UnstyledButton>
                  </Table.Td>
                  <Table.Td ta="right">{row.demoAccounts.toLocaleString()}</Table.Td>
                  <Table.Td ta="right" fw={600}>
                    {row.demoCount.toLocaleString()}
                  </Table.Td>
                  <Table.Td ta="right">{formatAverage(row.monthlyAverage)}</Table.Td>
                  <Table.Td ta="right">{formatAverage(row.yearlyAverage)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Skeleton>
  );
};

export default DistributorComparisonTable;
